import { Printer } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";

const SECTIONS = [
  {
    title: "Every month",
    items: [
      "Record and categorize all business transactions",
      "Send invoices promptly; follow up on overdue ones",
      "Pay bills on time; note anything that must wait and why",
      "Download or file bank and credit card statements",
      "Reconcile bank and credit card accounts against the books",
      "Set aside money for taxes in a separate account",
      "Review cash flow: what came in, what went out, what’s next",
    ],
  },
  {
    title: "Every quarter",
    items: [
      "Review estimated-payment questions with your tax preparer or planner",
      "Check vendor and contractor records (W-9s on file for anyone you pay)",
      "Review payroll records if you have a team",
      "Compare actual results against your budget; note the why",
      "Back up your financial records to a second location",
    ],
  },
  {
    title: "Every year end",
    items: [
      "Close out unreconciled accounts; chase missing statements",
      "Summarize 1099 contractors and confirm their details",
      "Inventory equipment and large purchases made during the year",
      "Compile a list of what changed in the business this year",
      "Meet with your accountant while the year is still open for planning",
    ],
  },
];

export default function ChecklistBusiness() {
  return (
    <>
      <Seo
        title="Business Accounting Checklist (Printable) | Directive Optive Solutions"
        description="A free printable business accounting checklist: monthly bookkeeping habits, quarterly reviews, and year-end tasks that keep small business books ready."
        path="/resources/business-accounting-checklist"
      />
      <PageHeader
        eyebrow="Free resource. No email required"
        title="Business Accounting Checklist"
        lead="Small habits keep books ready for anything: taxes, loans, or a big decision. Print this page and check things off. Or hand it to whoever keeps your books."
        image="/img/photo-bookkeeping-hands.webp"
        imageAlt="Hands organizing receipts and records with a calculator"
      />

      <section className="py-24 lg:py-32" data-testid="business-checklist-section">
        <div className="container-shell max-w-4xl">
          <Reveal>
            <button onClick={() => window.print()} data-testid="business-checklist-print-button" className="btn-primary no-print">
              <Printer className="h-4 w-4" /> Print this checklist
            </button>
          </Reveal>
          <div className="mt-14 space-y-14">
            {SECTIONS.map((s, si) => (
              <Reveal key={s.title} delay={si * 0.06}>
                <h2 className="font-serif text-2xl text-olive sm:text-3xl">{s.title}</h2>
                <ul className="mt-6 space-y-3.5">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 border-b border-line pb-3.5 text-base leading-relaxed text-moss" data-testid={`business-checklist-item-${s.items.indexOf(item) + 1}`}>
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-olive/50" aria-hidden />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 rounded-md border border-olive/25 bg-sage p-8 no-print">
            <p className="text-sm leading-relaxed text-moss"><span className="font-bold text-ink">General guidance, not tax advice.</span> Every business is different. Scope and specifics get confirmed in a conversation. If you’d like help taking these off your plate, we’re one consultation away.</p>
          </Reveal>
        </div>
      </section>

      <CtaSection heading="Would you rather not do the checklist at all?" body="That’s what we’re for. Book a consultation and we’ll keep the habits running for you." />
    </>
  );
}
